import React, { useState, useEffect } from 'react';
import { Search, BookOpen, GraduationCap, X, AlertCircle } from 'lucide-react';
import cursosService from '../services/cursoService';

// Componente para seleção de curso com validações
const CursoSelector = ({ 
  selectedCurso, 
  onSelectCurso, 
  error,
  disabled = false,
  placeholder = "Selecione um curso...",
  showDetails = true,
  alunoId = null // Para verificar conflitos de matrícula
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [validacoes, setValidacoes] = useState({});

  // Buscar cursos baseado no termo de pesquisa
  useEffect(() => {
    const buscarCursos = async () => {
      if (!searchTerm || searchTerm.length < 2) {
        setCursos([]);
        return;
      }

      setSearchLoading(true);
      try {
        const response = await cursosService.buscarCursos(searchTerm);
        if (response.success) {
          setCursos(response.data || []);
        } else {
          setCursos([]);
        }
      } catch (error) {
        console.error('Erro ao buscar cursos:', error);
        setCursos([]);
      } finally {
        setSearchLoading(false);
      }
    };

    const timeoutId = setTimeout(buscarCursos, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Listar cursos iniciais quando abrir o dropdown
  useEffect(() => {
    if (isOpen && !searchTerm && cursos.length === 0) {
      const carregarCursosIniciais = async () => {
        setLoading(true);
        try {
          const response = await cursosService.listarCursos({ 
            limit: 50, 
            page: 1,
            ativo: true // Apenas cursos ativos
          });
          if (response.success) {
            setCursos(response.data || []);
          }
        } catch (error) {
          console.error('Erro ao carregar cursos iniciais:', error);
        } finally {
          setLoading(false);
        }
      };
      carregarCursosIniciais();
    }
  }, [isOpen, searchTerm, cursos.length]);

  // Validar seleção de curso para o aluno
  useEffect(() => {
    const validarSelecao = async () => {
      if (!alunoId || cursos.length === 0) {
        setValidacoes({});
        return;
      }

      try {
        // Verificar disponibilidade para cada curso
        const validacoesPromises = cursos.map(async (curso) => {
          try {
            const matriculaService = await import('../services/matriculaService');
            const response = await matriculaService.default.verificarDisponibilidade(alunoId, curso.id);
            return {
              cursoId: curso.id,
              disponivel: response.success && response.data?.disponivel,
              motivo: response.data?.motivo || response.error,
              conflitos: response.data?.conflitos || []
            };
          } catch (error) {
            return {
              cursoId: curso.id,
              disponivel: false,
              motivo: 'Erro ao verificar disponibilidade',
              conflitos: []
            };
          }
        });

        const resultados = await Promise.all(validacoesPromises);
        const validacoesMap = {};
        resultados.forEach(result => {
          validacoesMap[result.cursoId] = result;
        });
        setValidacoes(validacoesMap);
      } catch (error) {
        console.error('Erro ao validar cursos:', error);
        setValidacoes({});
      }
    };

    if (alunoId && cursos.length > 0) {
      const timeoutId = setTimeout(validarSelecao, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [alunoId, cursos]);

  const handleSelectCurso = (curso) => {
    // Verificar se o curso está disponível
    const validacao = validacoes[curso.id];
    if (alunoId && validacao && !validacao.disponivel) {
      // Ainda permite a seleção, mas mostra o aviso
      console.warn(`Curso não disponível: ${validacao.motivo}`);
    }
    
    onSelectCurso(curso);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClearSelection = () => {
    onSelectCurso(null);
    setSearchTerm('');
  };

  const formatarDisplayCurso = (curso) => {
    if (!curso) return '';
    return `${curso.nome}${curso.codigo ? ` (${curso.codigo})` : ''}`;
  };

  const getCursoStatusInfo = (curso) => {
    const validacao = validacoes[curso.id];
    
    if (!alunoId || !validacao) {
      return { 
        disponivel: true, 
        className: '', 
        icon: null, 
        message: '' 
      };
    }

    if (validacao.disponivel) {
      return {
        disponivel: true,
        className: 'border-green-200 bg-green-50',
        icon: <GraduationCap className="w-4 h-4 text-green-600" />,
        message: 'Disponível para matrícula'
      };
    } else {
      return {
        disponivel: false,
        className: 'border-orange-200 bg-orange-50',
        icon: <AlertCircle className="w-4 h-4 text-orange-600" />,
        message: validacao.motivo || 'Não disponível'
      };
    }
  };

  return (
    <div className="relative">
      {/* Campo de seleção */}
      <div className="relative">
        <div
          className={`
            w-full px-4 py-3 border border-gray-300 rounded-lg 
            bg-white cursor-pointer transition-colors duration-200
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-blue-400'}
            ${error ? 'border-red-400 bg-red-50' : ''}
            ${isOpen ? 'border-blue-500 ring-2 ring-blue-200' : ''}
          `}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BookOpen className="w-5 h-5 text-gray-400" />
              <span className={selectedCurso ? 'text-gray-900' : 'text-gray-500'}>
                {selectedCurso ? formatarDisplayCurso(selectedCurso) : placeholder}
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              {selectedCurso && !disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearSelection();
                  }}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  title="Limpar seleção"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              
              <Search className={`w-5 h-5 transition-transform duration-200 ${
                isOpen ? 'rotate-180 text-blue-500' : 'text-gray-400'
              }`} />
            </div>
          </div>
        </div>

        {/* Dropdown de opções */}
        {isOpen && !disabled && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-w-full">
            {/* Campo de busca */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Digite o nome ou código do curso..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-base border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Lista de cursos com altura aumentada */}
            <div className="max-h-80 overflow-y-auto overscroll-contain">
              {loading || searchLoading ? (
                <div className="p-4 text-center text-gray-500">
                  <div className="animate-spin inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  <p className="mt-2 text-base">Carregando cursos...</p>
                </div>
              ) : cursos.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {searchTerm ? (
                    <>
                      <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-base">Nenhum curso encontrado para "{searchTerm}"</p>
                      <p className="text-sm mt-1">Tente buscar por nome ou código</p>
                    </>
                  ) : (
                    <>
                      <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-base">Digite pelo menos 2 caracteres para buscar</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {cursos.map((curso) => {
                    const statusInfo = getCursoStatusInfo(curso);
                    
                    return (
                      <div
                        key={curso.id}
                        className={`p-4 hover:bg-blue-50 cursor-pointer transition-colors duration-150 ${
                          !statusInfo.disponivel ? 'opacity-75' : ''
                        }`}
                        onClick={() => handleSelectCurso(curso)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className="font-medium text-base text-gray-900 truncate">{curso.nome}</h4>
                              {statusInfo.icon}
                            </div>
                            
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm text-gray-600">
                              {curso.codigo && (
                                <span className="font-mono bg-gray-100 px-2 py-1 rounded w-fit">
                                  {curso.codigo}
                                </span>
                              )}
                              {curso.carga_horaria && (
                                <span className="font-medium whitespace-nowrap">
                                  {curso.carga_horaria}h
                                </span>
                              )}
                              {curso.modalidade && (
                                <span className="capitalize">{curso.modalidade}</span>
                              )}
                            </div>

                            {showDetails && curso.descricao && (
                              <p className="mt-2 text-sm text-gray-500 line-clamp-2">
                                {curso.descricao}
                              </p>
                            )}

                            {alunoId && statusInfo.message && (
                              <div className={`mt-2 p-2 rounded-md border ${statusInfo.className}`}>
                                <p className="text-sm font-medium flex items-center space-x-1">
                                  {statusInfo.icon}
                                  <span>{statusInfo.message}</span>
                                </p>
                              </div>
                            )}
                          </div>
                          
                          <BookOpen className="w-5 h-5 text-gray-400 ml-3 mt-1 flex-shrink-0" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Rodapé com informações */}
            {cursos.length > 0 && (
              <div className="p-2 bg-gray-50 border-t border-gray-200 text-sm text-gray-500 text-center">
                {cursos.length} curso{cursos.length !== 1 ? 's' : ''} encontrado{cursos.length !== 1 ? 's' : ''}
                {alunoId && (
                  <span className="ml-2 text-blue-600">
                    • Verificando disponibilidade...
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Informações adicionais do curso selecionado */}
      {selectedCurso && showDetails && (
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">
                {selectedCurso.nome}
              </p>
              <div className="flex items-center space-x-3 text-xs text-blue-700 mt-1">
                {selectedCurso.codigo && (
                  <span>Código: {selectedCurso.codigo}</span>
                )}
                {selectedCurso.duracao && (
                  <span>Duração: {selectedCurso.duracao}</span>
                )}
                {selectedCurso.modalidade && (
                  <span>{selectedCurso.modalidade}</span>
                )}
              </div>
              
              {selectedCurso.descricao && (
                <p className="text-xs text-blue-600 mt-2 line-clamp-3">
                  {selectedCurso.descricao}
                </p>
              )}
            </div>

            {alunoId && validacoes[selectedCurso.id] && (
              <div className="ml-3">
                {validacoes[selectedCurso.id].disponivel ? (
                  <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <GraduationCap className="w-3 h-3 mr-1" />
                    Disponível
                  </div>
                ) : (
                  <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Conflito
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mensagem de erro */}
      {error && (
        <p className="mt-2 text-sm text-red-600 flex items-center">
          <X className="w-4 h-4 mr-1" />
          {error}
        </p>
      )}
    </div>
  );
};

export default CursoSelector;